/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { GroupRepresentation } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
export class AdminGroupsApi extends BaseApi {
    activate(groupId) {
        throwIfNotDefined(groupId, 'groupId');
        const pathParams = {
            groupId
        };
        return this.post({
            path: '/api/enterprise/admin/groups/{groupId}/action/activate',
            pathParams
        });
    }
    addAllUsersToGroup(groupId) {
        throwIfNotDefined(groupId, 'groupId');
        const pathParams = {
            groupId
        };
        return this.post({
            path: '/api/enterprise/admin/groups/{groupId}/add-all-users',
            pathParams
        });
    }
    addGroupCapabilities(groupId, addGroupCapabilitiesRepresentation) {
        throwIfNotDefined(groupId, 'groupId');
        throwIfNotDefined(addGroupCapabilitiesRepresentation, 'addGroupCapabilitiesRepresentation');
        const pathParams = {
            groupId
        };
        return this.post({
            path: '/api/enterprise/admin/groups/{groupId}/capabilities',
            pathParams,
            bodyParam: addGroupCapabilitiesRepresentation
        });
    }
    addGroupMember(groupId, userId) {
        throwIfNotDefined(groupId, 'groupId');
        throwIfNotDefined(userId, 'userId');
        const pathParams = {
            groupId,
            userId
        };
        return this.post({
            path: '/api/enterprise/admin/groups/{groupId}/members/{userId}',
            pathParams
        });
    }
    addRelatedGroup(groupId, relatedGroupId, type) {
        throwIfNotDefined(groupId, 'groupId');
        throwIfNotDefined(relatedGroupId, 'relatedGroupId');
        throwIfNotDefined(type, 'type');
        const pathParams = {
            groupId,
            relatedGroupId
        };
        const queryParams = {
            type
        };
        return this.post({
            path: '/api/enterprise/admin/groups/{groupId}/related-groups/{relatedGroupId}',
            pathParams,
            queryParams
        });
    }
    createNewGroup(groupRepresentation) {
        throwIfNotDefined(groupRepresentation, 'groupRepresentation');
        return this.post({
            path: '/api/enterprise/admin/groups',
            bodyParam: groupRepresentation,
            returnType: GroupRepresentation
        });
    }
    deleteGroupCapability(groupId, groupCapabilityId) {
        throwIfNotDefined(groupId, 'groupId');
        throwIfNotDefined(groupCapabilityId, 'groupCapabilityId');
        const pathParams = {
            groupId,
            groupCapabilityId
        };
        return this.delete({
            path: '/api/enterprise/admin/groups/{groupId}/capabilities/{groupCapabilityId}',
            pathParams
        });
    }
    deleteGroupMember(groupId, userId) {
        throwIfNotDefined(groupId, 'groupId');
        throwIfNotDefined(userId, 'userId');
        const pathParams = {
            groupId,
            userId
        };
        return this.delete({
            path: '/api/enterprise/admin/groups/{groupId}/members/{userId}',
            pathParams
        });
    }
    deleteGroup(groupId) {
        throwIfNotDefined(groupId, 'groupId');
        const pathParams = {
            groupId
        };
        return this.delete({
            path: '/api/enterprise/admin/groups/{groupId}',
            pathParams
        });
    }
    deleteRelatedGroup(groupId, relatedGroupId) {
        throwIfNotDefined(groupId, 'groupId');
        throwIfNotDefined(relatedGroupId, 'relatedGroupId');
        const pathParams = {
            groupId,
            relatedGroupId
        };
        return this.delete({
            path: '/api/enterprise/admin/groups/{groupId}/related-groups/{relatedGroupId}',
            pathParams
        });
    }
    getCapabilities(groupId) {
        throwIfNotDefined(groupId, 'groupId');
        const pathParams = {
            groupId
        };
        return this.get({
            path: '/api/enterprise/admin/groups/{groupId}/potential-capabilities',
            pathParams
        });
    }
    getGroupUsers(groupId, opts) {
        throwIfNotDefined(groupId, 'groupId');
        const pathParams = {
            groupId
        };
        return this.get({
            path: '/api/enterprise/admin/groups/{groupId}/users',
            pathParams,
            queryParams: opts
        });
    }
    getGroup(groupId, opts) {
        throwIfNotDefined(groupId, 'groupId');
        const pathParams = {
            groupId
        };
        return this.get({
            path: '/api/enterprise/admin/groups/{groupId}',
            pathParams,
            queryParams: opts
        });
    }
    getGroups(opts) {
        return this.get({
            path: '/api/enterprise/admin/groups',
            queryParams: opts
        });
    }
    getRelatedGroups(groupId) {
        throwIfNotDefined(groupId, 'groupId');
        const pathParams = {
            groupId
        };
        return this.get({
            path: '/api/enterprise/admin/groups/{groupId}/related-groups',
            pathParams
        });
    }
    updateGroup(groupId, groupRepresentation) {
        throwIfNotDefined(groupId, 'groupId');
        throwIfNotDefined(groupRepresentation, 'groupRepresentation');
        const pathParams = {
            groupId
        };
        return this.put({
            path: '/api/enterprise/admin/groups/{groupId}',
            pathParams,
            bodyParam: groupRepresentation,
            returnType: GroupRepresentation
        });
    }
}
